import { EmployeePayTypeEnum } from '../user-roles/user-roles.types';

export enum PayrollFrequencies {
  WEEKLY = 'Weekly',
  BI_WEEKLY = 'Biweekly',
  MONTHLY = 'Monthly',
  SEMI_MONTHLY = 'Semimonthly',
}
export enum PayrollTypes {
  Regular = 'Regular',
  OffCycle = 'OffCycle',
  Amendment = 'Amendment',
}
export enum PayScheduleStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  ToBeActive = 'ToBeActive',
}

export enum EarningTypes {
  Fixed = 'Fixed',
  Hourly = 'Hourly',
  Salary = 'Salary',
  Salaried = 'Salaried',
  Overtime = 'Overtime',
  DoubleOvertime = 'Double Overtime',
  WeeklyOverTime = 'Weekly Overtime',
  PaidHoliday = 'Paid Holiday',
  Pto = 'Pto',
  Sick = 'Sick',
  Piece = 'Piece',
  RestAndRecovery = 'Rest And Recovery',
  NonProductive = 'Non Productive',
  NonHourlyRegular = 'Non Hourly Regular',
  CashTips = 'Cash Tips',
  PaycheckTips = 'Paycheck Tips',
  Bonus = 'Bonus',
  Commission = 'Commission',
  GroupTermLife = 'Group Term Life',
  OtherImputed = 'Other Imputed',
  Severance = 'Severance',
  ShareholderBenefits2 = '2% Shareholder Benefits',
  ShareholderHsa2 = '2% Shareholder HSA',
  AgricultureBonus = 'Agriculture Bonus',
  AgricultureSeverance = 'Agriculture Severance',
  WeightedOvertime = 'Weighted Overtime',
  WeightedDoubleOvertime = 'Weighted Double Overtime',
  TipCreditAdjustment = 'Tip Credit Adjustment',
}

export interface PayScheduleRequest {
  name: string;
  payFrequency: PayrollFrequencies;
  firstPayDate: Date;
  secondPayDate: Date;
  firstPeriodEndDate: Date;
  active: boolean;
}

export interface PayScheduleType {
  name: string;
  payFrequency: PayrollFrequencies;
  firstPayday: Date;
  secondPayday?: Date;
  firstPeriodEnd: Date;
  status: PayScheduleStatus;
}

export enum PayrollStatus {
  PROCESSING = 'processing',
  DRAFT = 'draft',
  FAILED = 'failed',
  PARTIAL = 'partial',
  PAID = 'paid',
  PENDING = 'pending',
}

export enum PayrollPaymentType {
  DIRECT_DEPOSIT = 'direct_deposit',
  MANUAL = 'manual',
}

export enum PayrollByRoleName {
  ACCOUNTANT = 'Accountant',
  OTHER_PAYROLL_EXPENSE = 'otherPayrollExpense',
  NO_ROLE = '-',
}

export enum ChartOfAccountIdByName {
  Bank_1002 = '1002',
  Vendor_2000 = '2000',
  Tender_1200 = '1200',
  Inventory_Asset_1300 = '1300',
  Net_Pay_Liability_2200_01 = '2200-01',
  House_Drawer_Cash_1001_01 = '1001-01',
  Payroll_Liabilities_2200 = '2200',
  Payroll_Tax_Expense_5300 = '5300',
  Other_Payroll_Expense_5220_06 = '5220-06',
  Other_Payroll_5220 = '5220',
  Accounting_Fees_6003_03 = '6003-03',
  CreditCard_Liability_2100 = '2100',
  Accounts_Receivable_1200 = '1200',
  Accounts_Payable_2000 = '2000',
  Cash_In_Safe_1001_02 = '1001-02',
  Processing_Fee_6001_14 = '6001-14',
  Food_Sales_4001_01 = '4001-01',
  Beer_Sales_4001_02 = '4001-02',
  Liquor_Sales_4001_03 = '4001-03',
  Wine_Sales_4001_04 = '4001-04',
  NA_Beverages_4001_05 = '4001-05',
  Sales_Tax_Payable_2300_02 = '2300-02',
  Gratuity_4003_01 = '4003-01',
  Service_Charges_4003_02 = '4003-02',
  Gift_Card_Outstanding_2300_04 = '2300-04',
  Cash_Short_6003_06 = '6003-06',
  EMPLOYEE_TIPS_PAYABLE_2200_02 = '2200-02',
  Food_Cost_5100 = '5100',
  Wastage_Log_5100_08 = '5100-08',
  Purchase_Discount_5100_09 = '5100-09',
  Gas_And_Vehicle_Expense_6001_11 = '6001-11',
  Ask_Client_Expense_9003_02 = '9003-02',
  Other_Expenses_5100_10 = '5100-10',
  Tax_Expense_5100_11 = '5100-11',
  Employee_Benefits_2200_07 = '2200-07',
  Fixed_Assets_1500 = '1500',
  Operation_Costs_6001 = '6001',
  General_And_Admin_6003 = '6003',
  Occupancy_6004 = '6004',
  Supplies_6005 = '6005',
  UNEXPLAINED_INCOME_9004 = '9004',
}

export enum PayrollPaymentMethod {
  DIRECT_DEPOSIT = 'Direct Deposit',
  MANUAL = 'Manual',
}

export enum ReconcillationAccountType {
  CREDIT_CARD = ' (CC)',
}
export interface PayrollDetail {
  paymentMethod: PayrollPaymentMethod;
  employeeName: string;
  employeeId: string;
  payrollItemId: string;
  netPay: number;
  totalTaxDeduction: number;
}

export interface Payroll {
  checkHqPayrollId: string;
  payday: string;
  items: PayrollDetail[];
}
export enum TaxPayer {
  COMPANY = 'company',
  EMPLOYEE = 'employee',
}

export interface PayrollTaxes {
  totalAmount: number;
  description: string;
  payer?: TaxPayer;
}

export const FICA_SUB_TAXES_KEYWORDS = ['fica', 'medicare'];

export enum TaxesGroup {
  FICA = 'FICA Payable',
  STATE_TAXES = 'State Unemployment Insurance (SUI)/Employee training Tax Payable',
  FEDERAL_UNEMPLOYMENT_TAX_CHECKHQ_NAME = 'Federal Unemployment Tax',
  FEDERAL_INCOME_TAX_CHECKHQ_NAME = 'Federal Income Tax',
  FEDERAL_UNEMPLOYMENT_TAX_SYSTEM_NAME = 'Federal Unemployment (940) Payable',
  FEDERAL_INCOME_TAX_SYSTEM_NAME = 'Federal Taxes (941/944) Payable',
}

export interface CheckHQPayrollTaxItem {
  tax: string;
  payer: TaxPayer;
  amount: string;
  remittable: boolean;
  description: string;
}

export const NonIngredientAssignableChartsOfAccounts = [
  ChartOfAccountIdByName.Fixed_Assets_1500,
  ChartOfAccountIdByName.Operation_Costs_6001,
  ChartOfAccountIdByName.General_And_Admin_6003,
  ChartOfAccountIdByName.Occupancy_6004,
  ChartOfAccountIdByName.Supplies_6005,
];
export interface PayrollCustomData {
  label: string;
  val: string | number;
}
export interface PayDataMetaData {
  name: string;
  roleData: string;
  employeeTotal: number;
}
interface PayData {
  Type: string;
  Rate: number;
  Hours: number;
  Role?: string;
  Amount: number;
  metadata?: string;
}

interface EmployeeMeta {
  [roleId: string]: {
    roleId: number;
    role: string;
    salary: number;
    employeeId: number;
    payType: EmployeePayTypeEnum;
    metadata: any;
  };
}

export interface EmployeeDataForUpdate {
  checkHqId: string;
  fullname: string;
  typeText: string;
  amountText: number;
  netPay: number | null;
  employeeMeta: EmployeeMeta;
  payrollData: PayrollCustomData[];
  payData: PayData[];
  headingText: string;
  employeeId: string;
}
